import MediaStreamRecorder from 'msr';
import merge from 'lodash/merge';
import app from '@/api/app';

const defaultParams = {
  size: 120 * 10000,
};

/**
 * from  https://github.com/lLK/scratch-audio/blob/develop/src/Loudness.js
 */
export class NavigatorLoudness {
  /**
   * Instrument and detect a loudness value from a local microphone.
   * @param {AudioContext=} audioContext - context to create nodes from for
   *     detecting loudness
   * @constructor
   */
  constructor(audioContext) {
    // noinspection JSUnresolvedVariable
    /**
     * AudioContext the mic will connect to and provide analysis of
     * @type {AudioContext}
     */
    this.audioContext = audioContext || new (window.AudioContext || window.webkitAudioContext)();

    /**
     * Are we connecting to the mic yet?
     * @type {Boolean}
     */
    this.connectingToMic = false;

    /**
     * microphone, for measuring loudness, with a level meter analyzer
     * @type {MediaStreamAudioSourceNode}
     */
    this.mic = null;
  }

  /**
   * Get the current loudness of sound received by the microphone.
   * Sound is measured in RMS and smoothed.
   * Some code adapted from Tone.js: https://github.com/Tonejs/Tone.js
   * @return {number} loudness scaled 0 to 100
   */
  getLoudness() {
    // The microphone has not been set up, so try to connect to it
    if (!this.mic && !this.connectingToMic) {
      this.connectingToMic = true; // prevent multiple connection attempts
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        /** @type any */
        this.audioStream = stream;
        this.mic = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.mic.connect(this.analyser);
        this.micDataArray = new Float32Array(this.analyser.fftSize);
      });
    }

    // If the microphone is set up and active, measure the loudness
    if (this.mic && this.audioStream.active) {
      this.analyser.getFloatTimeDomainData(this.micDataArray);
      let sum = 0;
      // compute the RMS of the sound
      for (let i = 0; i < this.micDataArray.length; i++) {
        // eslint-disable-next-line no-restricted-properties
        sum += Math.pow(this.micDataArray[i], 2);
      }
      let rms = Math.sqrt(sum / this.micDataArray.length);
      // smooth the value, if it is descending
      if (this._lastValue) {
        rms = Math.max(rms, this._lastValue * 0.6);
      }
      this._lastValue = rms;
      // Scale the measurement so it's more sensitive to quieter sounds
      rms *= 1.63;
      rms = Math.sqrt(rms);
      // Scale it up to 0-100 and round
      rms = Math.round(rms * 1000);
      // Prevent it from going above 100
      rms = Math.min(rms, 100);
      return rms;
    }

    // if there is no microphone input, return -1
    return -1;
  }

  dispose() {
    if (this.mic) {
      this.mic.disconnect();
    }
    if (this.audioContext) {
      this.audioContext.close().then();
    }
    if (this.audioStream) {
      this.audioStream.stop();
    }
  }
}

/**
 * @typedef RecordOptions
 * @property {(blob?: Blob, duration?: number) => void =} onSuccess
 * @property {(loudness: number) => void=} onLoudnessChange 音量变动回调
 * @property {function=} onGetAuthSuccess 成功授权后的回调
 * @property {function=} onGetAuthError 授权失败后的回调
 * @property {number=} size 切片大小, 这个参数有且仅有 {@link window.getUserMedia} 存在且没有桥的时候才有用
 */

/**
 * @typedef RecordInstance
 * @property {() => void} stop 停止录音
 * @property {() => Promise<import('@xigua/app-bridge').IRecord>=} upload 上传文件(因为有的地方上传文件可能不是 web 端);
 *      存在这个方法则表示当前是使用的桥来处理录音的
 */

/**
 * 录音
 * @param {RecordOptions} params
 * @returns {Promise<RecordInstance>}
 */
const record = async (params = {}) => {
  const p = merge(params, defaultParams);

  if (await app.caniuse('getRecorder')) {
    try {
      const appRecorder = app.getRecorder();
      await appRecorder.startRecording(({ db }) => {
        if (params.onLoudnessChange) {
          params.onLoudnessChange(((Math.abs(db) / 60) * 100) || 0);
        }
      });
      if (params.onGetAuthSuccess) {
        params.onGetAuthSuccess();
      }
      return {
        stop: async () => {
          if (params.onSuccess) {
            params.onSuccess();
          }
        },
        upload: () => appRecorder.stopRecording(),
      };
    } catch (e) {
      p.onGetAuthError && p.onGetAuthError();
    }
  }

  // 原有逻辑

  let mediaRecorder = null;
  const mediaError = () => {
    p.onGetAuthError && p.onGetAuthError();
  };

  const mediaSuccess = (stream) => {
    p.onGetAuthSuccess && p.onGetAuthSuccess();
    const timestamp = Date.now();
    mediaRecorder = new MediaStreamRecorder(stream);
    mediaRecorder.stream = stream;
    mediaRecorder.mimeType = 'audio/wav';
    mediaRecorder.ondataavailable = (blob) => {
      p.onSuccess && p.onSuccess(blob, (Date.now() - timestamp) / 1000);
    };
    // 切片大小 kb
    mediaRecorder.start(p.size);
  };

  if (!navigator.getUserMedia) throw new Error('not have navigator.getUserMedia, maybe you not https');

  navigator.getUserMedia({ audio: true }, mediaSuccess, mediaError);

  const nl = new NavigatorLoudness();
  const loudnessChangeTimer = setInterval(() => {
    if (params.onLoudnessChange) {
      params.onLoudnessChange(nl.getLoudness());
    }
  }, 100);

  return {
    stop: () => {
      clearInterval(loudnessChangeTimer);
      nl.dispose();
      if (mediaRecorder && mediaRecorder.stop) mediaRecorder.stop();
      if (mediaRecorder && mediaRecorder.stream && mediaRecorder.stream.stop) mediaRecorder.stream.stop();
    },
  };
};

export default record;
